import React from 'react';
import axios from 'axios';
import { useQuery } from 'react-query';
import Icon from '@mdi/react';
import { mdiAlertOutline, mdiCheckCircle } from '@mdi/js';
import CampoSeletorLivre from '@/components/Formulario/CampoSeletorLivre';
import VBox from '@/components/layout/VBox';
import type {
    RadiusGroupApplianceScope,
    RadiusVlanUsoDTO,
} from '@webip/shared/src/dto/radius_user';

/** Texto do campo → VLAN. undefined = texto inválido (não é tag 802.1Q). */
function parseVlan(txt: string): number | null | undefined {
    const t = txt.trim();
    if (!t) return null;
    if (!/^\d+$/.test(t)) return undefined;
    const n = Number(t);
    return n >= 1 && n <= 4094 ? n : undefined;
}

/**
 * Campo de VLAN do grupo/usuário RADIUS. Grava só o número (a tag 802.1Q é a
 * chave com as LANs — o grupo não se amarra a uma appliance), mas sugere as
 * VLANs que existem nas LANs do escopo e mostra, appliance por appliance, se a
 * tag escolhida existe lá. Aviso, não bloqueio: a VLAN pode terminar num
 * equipamento que o WebIP não gerencia.
 */
export default function CampoVlan({
    value,
    onChange,
    onValidChange,
    scope,
    applianceIds,
    herdada,
    label = 'VLAN',
}: {
    value: number | null;
    onChange: (v: number | null) => void;
    /** false enquanto o texto digitado não for uma VLAN válida. */
    onValidChange?: (ok: boolean) => void;
    scope: RadiusGroupApplianceScope;
    applianceIds: number[];
    /** Modo usuário: VLAN do grupo, aplicada quando o campo fica vazio. */
    herdada?: number | null;
    label?: string;
}) {
    const [texto, setTexto] = React.useState(value != null ? String(value) : '');
    const parsed = parseVlan(texto);
    const invalido = parsed === undefined;

    React.useEffect(() => {
        onValidChange?.(!invalido);
    }, [invalido, onValidChange]);

    const idsKey = scope === 'specific' ? [...applianceIds].sort((a, b) => a - b).join(',') : '';
    const consultar = scope === 'all' || (scope === 'specific' && applianceIds.length > 0);

    const query = useQuery(
        ['radius-vlans', scope, idsKey],
        async () => {
            const res = await axios.get<{ ok: boolean; result: RadiusVlanUsoDTO }>('/api/radius/vlans', {
                params: scope === 'specific' ? { appliance_ids: idsKey } : undefined,
                withCredentials: true,
            });
            return res.data.result;
        },
        { enabled: consultar, staleTime: 30000 },
    );
    const uso = consultar ? query.data : undefined;

    const sugestoes = React.useMemo(() => {
        const porVlan = new Map<number, string[]>();
        for (const r of uso?.redes ?? []) {
            const l = porVlan.get(r.vlan) ?? [];
            l.push(`${r.rede_nome} (${r.appliance_nome})`);
            porVlan.set(r.vlan, l);
        }
        return [...porVlan.entries()].map(([vlan, redes]) => ({
            id: String(vlan),
            label: `${vlan} — ${redes.join(', ')}`,
        }));
    }, [uso]);

    const efetiva = parsed ?? (parsed === null ? herdada ?? null : null);

    const onTexto = (v: string | null) => {
        const t = v ?? '';
        setTexto(t);
        const p = parseVlan(t);
        if (p !== undefined) onChange(p);
    };

    const placeholder = herdada !== undefined
        ? herdada != null ? `Herdar do grupo (${herdada})` : 'Herdar do grupo (sem VLAN)'
        : 'Sem VLAN';

    return (
        <VBox style={{ gap: '6px' }}>
            <CampoSeletorLivre
                label={label}
                value={texto}
                onChange={onTexto}
                options={sugestoes}
                placeholder={placeholder}
                nullable
                error={invalido ? 'Informe um número entre 1 e 4094' : undefined}
                hint={
                    efetiva == null
                        ? 'Vazio: o RADIUS não atribui VLAN e o AP/switch usa a dele.'
                        : 'Enviada como Tunnel-Private-Group-ID no Access-Accept. O AP/switch precisa estar com a VLAN liberada no trunk.'
                }
            />
            {!invalido && efetiva != null && (
                <QuadroCobertura
                    vlan={efetiva}
                    scope={scope}
                    semAppliances={scope === 'specific' && applianceIds.length === 0}
                    uso={uso}
                    carregando={query.isLoading && consultar}
                />
            )}
        </VBox>
    );
}

function QuadroCobertura({
    vlan,
    scope,
    semAppliances,
    uso,
    carregando,
}: {
    vlan: number;
    scope: RadiusGroupApplianceScope;
    semAppliances: boolean;
    uso: RadiusVlanUsoDTO | undefined;
    carregando: boolean;
}) {
    const caixa: React.CSSProperties = {
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 10px',
        fontSize: '13px',
    };
    const muted: React.CSSProperties = { color: 'var(--color-muted)' };

    if (scope === 'none') {
        return <div style={{ ...caixa, ...muted }}>O grupo está desativado em todas as appliances.</div>;
    }
    if (semAppliances) {
        return <div style={{ ...caixa, ...muted }}>Selecione as appliances do escopo para conferir a VLAN {vlan}.</div>;
    }
    if (carregando || !uso) {
        return <div style={{ ...caixa, ...muted }}>Conferindo onde a VLAN {vlan} existe…</div>;
    }
    if (!uso.appliances.length) {
        return <div style={{ ...caixa, ...muted }}>Nenhuma appliance no escopo.</div>;
    }

    return (
        <div style={caixa}>
            <div style={{ fontWeight: 500, marginBottom: '6px' }}>Onde a VLAN {vlan} existe</div>
            <VBox style={{ gap: '4px' }}>
                {uso.appliances.map((a) => {
                    const redes = uso.redes.filter((r) => r.appliance_id === a.id && r.vlan === vlan);
                    const ok = redes.length > 0;
                    return (
                        <div key={a.id} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                            <Icon
                                path={ok ? mdiCheckCircle : mdiAlertOutline}
                                size="16px"
                                color={ok ? 'var(--status-online)' : 'var(--status-warn)'}
                                style={{ flexShrink: 0, marginTop: '1px' }}
                            />
                            <span>
                                <strong>{a.nome}</strong>
                                {ok ? (
                                    <span style={muted}>
                                        {' — '}
                                        {redes
                                            .map((r) => `rede "${r.rede_nome}"${r.rede_ip ? ` (${r.rede_ip})` : ''}`)
                                            .join(', ')}
                                    </span>
                                ) : (
                                    <span style={muted}>
                                        {' — nenhuma rede nesta VLAN; usuários do grupo ficarão sem rede quando conectarem por lá'}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                })}
            </VBox>
        </div>
    );
}
